import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Lock, Calendar, CheckCircle2, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

function formatIndian(num) {
  if (!num && num !== 0) return '-';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(num);
}

export default function PWMErectionProgress({ projectId, filters }) {
  const [lineRows, setLineRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLine, setEditingLine] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const lineRes = await api.get(
        `/v1/projects/${projectId}/pwm/erection/lines${filters.agUg ? `?agUg=${filters.agUg}` : ''}`
      );
      setLineRows(lineRes.data?.lines || []);
    } catch (e) { console.error('Erection fetch failed', e); }
    setLoading(false);
  }, [projectId, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLineClick = (line) => {
    if (line.erectionGate !== 'READY') return;
    setEditingLine(line.lineId);
    setEditDate(new Date().toISOString().slice(0, 10));
  };

  const handleSaveErection = async () => {
    if (!editingLine || !editDate) return;
    setSaving(true);
    try {
      const r = await api.put(
        `/v1/projects/${projectId}/pwm/line-erection`,
        { erectionDate: editDate },
        { params: { lineId: editingLine } }
      );
      toast.success(`Line ${editingLine} erected (${r.data?.spoolsErected || 0} spools rolled up)`);
      setEditingLine(null);
      fetchData();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message || detail.error : detail;
      toast.error(msg || 'Erection blocked by gate enforcement');
    }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;

  const renderLineErectionGate = (line) => {
    const isEditing = editingLine === line.lineId;

    if (isEditing) {
      return (
        <div className="flex items-center gap-1" data-testid={`line-erection-edit-${line.lineId}`}>
          <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)}
            className="text-[9px] px-1 py-0.5 rounded border w-24"
            style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            data-testid={`line-erection-date-input-${line.lineId}`} />
          <button onClick={handleSaveErection} disabled={saving} className="p-0.5 rounded" style={{ color: '#22c55e' }}
            data-testid={`line-erection-save-${line.lineId}`}>
            <CheckCircle2 className="w-3 h-3" />
          </button>
          <button onClick={() => setEditingLine(null)} className="p-0.5 rounded" style={{ color: '#ef4444' }}
            data-testid={`line-erection-cancel-${line.lineId}`}>
            <X className="w-3 h-3" />
          </button>
        </div>
      );
    }

    if (line.erectionGate === 'ERECTED') {
      const d = line.erectionDate;
      return (
        <span className="text-[9px] font-mono font-bold" style={{ color: '#22c55e' }}
          data-testid={`line-erection-complete-${line.lineId}`}>
          {typeof d === 'string' ? d.slice(0, 10) : 'Erected'}
        </span>
      );
    }

    if (line.erectionGate === 'LOCKED') {
      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center gap-0.5 cursor-not-allowed opacity-50"
                data-testid={`line-erection-locked-${line.lineId}`}>
                <Lock className="w-3 h-3" style={{ color: '#ef4444' }} />
                <span className="text-[8px]" style={{ color: '#ef4444' }}>Locked</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-[10px] max-w-[220px]">
              <p className="font-semibold text-red-400">Line Erection Blocked</p>
              <p>{line.lockReason || 'Not all spools dispatched'}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    // READY
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button onClick={() => handleLineClick(line)}
              className="flex items-center justify-center gap-0.5 cursor-pointer hover:opacity-70"
              data-testid={`line-erection-ready-${line.lineId}`}>
              <Calendar className="w-3 h-3" style={{ color: '#3b82f6' }} />
              <span className="text-[8px]" style={{ color: '#3b82f6' }}>Set Date</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-[10px]">All spools dispatched · click to record line erection date</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Roll-up totals for the Line Erection summary card
  const totalLineIM = lineRows.reduce((s, l) => s + (l.totalInchMeter || 0), 0);
  const erectedLineIM = lineRows.reduce((s, l) => s + (l.erectedInchMeter || 0), 0);
  const totalLines = lineRows.length;
  const erectedLines = lineRows.filter(l => l.erectionGate === 'ERECTED').length;
  const linePct = totalLineIM > 0 ? (erectedLineIM / totalLineIM * 100) : 0;

  return (
    <div className="space-y-3" data-testid="pwm-erection-progress">
      {/* Line Erection summary card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
          data-testid="erection-line-card">
          <p className="text-[10px] font-medium mb-1" style={{ color: '#22c55e' }}>Line Erection</p>
          <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{linePct.toFixed(1)}%</p>
          <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
            {formatIndian(erectedLineIM)} / {formatIndian(totalLineIM)} IM
          </p>
          <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
            {erectedLines} / {totalLines} lines
          </p>
        </div>
      </div>

      {/* Gate Legend */}
      <div className="flex items-center gap-3 p-2 rounded-lg" style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}
        data-testid="erection-gate-legend">
        <span className="text-[9px] font-medium" style={{ color: 'var(--text-muted)' }}>Gate Legend:</span>
        <span className="flex items-center gap-1 text-[9px]"><Lock className="w-3 h-3" style={{ color: '#ef4444' }} /> Blocked (not all spools dispatched)</span>
        <span className="flex items-center gap-1 text-[9px]"><Calendar className="w-3 h-3" style={{ color: '#3b82f6' }} /> Ready for erection</span>
        <span className="flex items-center gap-1 text-[9px]"><CheckCircle2 className="w-3 h-3" style={{ color: '#22c55e' }} /> Erected</span>
      </div>

      {/* Line-Level Table with Gate Status — Erection happens at line level */}
      <div className="rounded-lg border overflow-x-auto" style={{ borderColor: 'var(--border-color)' }}>
        <table className="w-full text-[10px]" data-testid="erection-line-table">
          <thead>
            <tr style={{ background: 'var(--card-bg-alt)' }}>
              <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Line ID</th>
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>AG/UG</th>
              <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Area</th>
              <th className="p-2 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Total ID</th>
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Spools</th>
              <th className="p-2 text-center font-medium" style={{ color: '#3b82f6' }}>Dispatched</th>
              <th className="p-2 text-right font-medium" style={{ color: 'var(--text-muted)' }}>IM Total</th>
              <th className="p-2 text-right font-medium" style={{ color: '#22c55e' }}>IM Erected</th>
              <th className="p-2 text-center font-medium" style={{ color: '#22c55e', borderLeft: '2px solid var(--border-color)' }}>Erection Gate</th>
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {lineRows.map(line => (
              <tr key={line.lineId} style={{ borderTop: '1px solid var(--border-color)', background: 'var(--card-bg)' }} data-testid={`erec-line-${line.lineId}`}>
                <td className="p-2 font-mono font-semibold" style={{ color: 'var(--accent-primary)' }}>{line.lineId}</td>
                <td className="p-2 text-center" style={{ color: line.agUg === 'AG' ? '#3b82f6' : '#f59e0b' }}>{line.agUg}</td>
                <td className="p-2" style={{ color: 'var(--text-muted)' }}>{line.area || '-'}</td>
                <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{formatIndian(line.totalLineInchDia)}</td>
                <td className="p-2 text-center" style={{ color: 'var(--text-primary)' }}>{line.totalSpools}</td>
                <td className="p-2 text-center font-mono" style={{ color: line.dispatchedSpools === line.totalSpools && line.totalSpools > 0 ? '#22c55e' : '#3b82f6' }}>
                  {line.dispatchedSpools} / {line.totalSpools}
                </td>
                <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{formatIndian(line.totalInchMeter)}</td>
                <td className="p-2 text-right font-mono" style={{ color: '#22c55e' }}>{formatIndian(line.erectedInchMeter)}</td>
                <td className="p-2 text-center" style={{ borderLeft: '2px solid var(--border-color)' }}>
                  {renderLineErectionGate(line)}
                </td>
                <td className="p-2 text-center text-[9px]" style={{ color: line.erectionStatus === 'ERECTED' ? '#22c55e' : 'var(--text-muted)' }}>
                  {line.erectionStatus}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
        {lineRows.length} lines
      </p>
    </div>
  );
}
